import React, { useEffect, useMemo, useState } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import { toast } from 'sonner';
import { SlidersHorizontal, ChevronDown, ChevronRight, RotateCcw, Save, Check, Ban, Loader2, Info } from 'lucide-react';

const GROUP_ORDER = ['InvictaCare', 'InvictaFit', 'InvictaDance', 'InvictaBeauty', 'InvictaDental', 'InvictaAuto', 'Weitere'];

export default function BranchModulesPage() {
  const [modules, setModules] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [blocked, setBlocked] = useState({});   // branche -> Set(keys)
  const [server, setServer] = useState({});     // branche -> Set(keys) (Snapshot)
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await superAdminApi.listIndustryModules();
      setModules(data.modules || []);
      setIndustries(data.industries || []);
      const map = {}; const snap = {};
      (data.industries || []).forEach((i) => {
        map[i.branche] = new Set(i.blocked_modules || []);
        snap[i.branche] = new Set(i.blocked_modules || []);
      });
      setBlocked(map); setServer(snap);
    } catch (e) {
      toast.error('Branchen-Regeln konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (branche, key) => {
    setBlocked((prev) => {
      const next = new Set(prev[branche] || []);
      if (next.has(key)) next.delete(key); else next.add(key);
      return { ...prev, [branche]: next };
    });
  };

  const isDirty = (branche) => {
    const a = blocked[branche] || new Set();
    const b = server[branche] || new Set();
    if (a.size !== b.size) return true;
    for (const k of a) if (!b.has(k)) return true;
    return false;
  };

  const save = async (branche) => {
    setSaving(branche);
    try {
      await superAdminApi.setIndustryModules(branche, Array.from(blocked[branche] || []));
      toast.success('Branchen-Regel gespeichert – Änderungen gelten sofort serverseitig');
      await load();
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(null);
    }
  };

  const reset = async (branche) => {
    setSaving(branche);
    try {
      await superAdminApi.resetIndustryModules(branche);
      toast.success('Auf Standard zurückgesetzt');
      await load();
    } catch (e) {
      toast.error('Zurücksetzen fehlgeschlagen');
    } finally {
      setSaving(null);
    }
  };

  const groups = useMemo(() => {
    const by = {};
    industries.forEach((i) => { (by[i.group] = by[i.group] || []).push(i); });
    return GROUP_ORDER.filter((g) => by[g]).map((g) => ({ group: g, items: by[g] }));
  }, [industries]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500" data-testid="bm-loading">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Branchen-Regeln werden geladen …
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branch-modules-page">
      <div>
        <h1 className="flex items-center gap-2.5 text-2xl font-semibold text-slate-900">
          <SlidersHorizontal className="h-6 w-6 text-indigo-600" /> Branchen-Module
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">
          Lege pro Branche fest, welche Module <span className="font-medium text-slate-700">gesperrt</span> sind.
          Jeder Mandant erhält beim Onboarding seine Branche – gesperrte Module werden dann automatisch
          in der Sidebar und serverseitig ausgeblendet, ohne dass du pro Mandant etwas abwählen musst.
        </p>
      </div>

      <div className="flex items-start gap-2.5 rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-800" data-testid="bm-hint">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Standardmäßig sind nur fachspezifische Module für fremde Branchen gesperrt (z.&nbsp;B. Zahnschema nur für
          Zahnmedizin, Werkstattaufträge nur für Garage). Ein <span className="font-medium">roter</span> Chip = gesperrt,
          <span className="font-medium"> grün</span> = erlaubt. Klick zum Umschalten.
        </span>
      </div>

      {groups.map(({ group, items }) => (
        <div key={group} className="space-y-3" data-testid={`bm-group-${group}`}>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{group}</div>
          {items.map((ind) => {
            const set = blocked[ind.branche] || new Set();
            const isOpen = open === ind.branche;
            const dirty = isDirty(ind.branche);
            return (
              <div key={ind.branche} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <button
                  type="button"
                  data-testid={`bm-toggle-${ind.branche}`}
                  onClick={() => setOpen(isOpen ? null : ind.branche)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50"
                >
                  <div className="flex items-center gap-3">
                    {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                    <div>
                      <div className="font-medium text-slate-900">{ind.label}</div>
                      <div className="text-xs text-slate-500">
                        {set.size === 0 ? 'Alle Module erlaubt' : `${set.size} Modul${set.size === 1 ? '' : 'e'} gesperrt`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {ind.customized && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">angepasst</span>
                    )}
                    {dirty && (
                      <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-700">ungespeichert</span>
                    )}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-slate-100 px-5 py-4">
                    <div className="flex flex-wrap gap-2" data-testid={`bm-chips-${ind.branche}`}>
                      {modules.map((m) => {
                        const isBlocked = set.has(m.key);
                        return (
                          <button
                            key={m.key}
                            type="button"
                            data-testid={`bm-${ind.branche}-mod-${m.key}`}
                            onClick={() => toggle(ind.branche, m.key)}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                              isBlocked
                                ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
                                : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                            }`}
                            title={isBlocked ? 'Gesperrt – klicken zum Erlauben' : 'Erlaubt – klicken zum Sperren'}
                          >
                            {isBlocked ? <Ban className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                            {m.name}
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-4 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        data-testid={`bm-reset-${ind.branche}`}
                        onClick={() => reset(ind.branche)}
                        disabled={saving === ind.branche || !ind.customized}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                      >
                        <RotateCcw className="h-4 w-4" /> Standard
                      </button>
                      <button
                        type="button"
                        data-testid={`bm-save-${ind.branche}`}
                        onClick={() => save(ind.branche)}
                        disabled={saving === ind.branche || !dirty}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-40"
                      >
                        {saving === ind.branche ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Speichern
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
